<template>
  <div>
    <b-row>
      <b-col cols="12" md="6">
        <small class="text-muted">
          Current Page: {{ currentPage }}. Total rows on preview: {{ rows }}
        </small>
        <small class="d-block text-warning"> * Can preview up to 1000 rows only</small>
      </b-col>
      <b-col>
        <b-pagination
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            size="sm"
            align="right"
            limit="10"
            v-if="rows > 0"
        ></b-pagination>
      </b-col>
    </b-row>
    <b-table
        class="smartgrabber-table"
        bordered
        outlined
        small
        responsive="md"
        sticky-header="85vh"
        :items="items"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
        :busy="isBusy"
        :sort-compare="CustomSortFunction"
        :filter="filter"
        :filter-included-fields="filterOn"
    >
      <template v-slot:table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import _ from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export default {
  name: 'DataViewer',
  props: {
    items: {type: Array},
    fields: {type: Array},
    isBusy: {type: Boolean, default: false},
  },
  data() {
    return {
      perPage: 100,
      currentPage: 1,
      filter: '',
      filterOn: [],
    };
  },
  computed: {
    rows() {
      return this.items.length;
    },
  },
  methods: {
    FormatInteger(number) {
      return new Intl.NumberFormat().format(number);
    },
    FormatDecimal(number) {
      return new Intl.NumberFormat().format(number);
    },
    FormatDate(date) {
      return date ? Intl.DateTimeFormat().format(dayjs(date)) : '';
    },
    CustomSortFunction(aRow, bRow, key) {
      //(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale)
      const fields = _.cloneDeep(this.fields);
      const field = _.find(fields, {key: key});

      let aValue = aRow[key];
      let bValue = bRow[key];
      let a, b;

      //for measures
      if (field.class.includes('smartgrabber-measure')) {
        if (parseFloat(aValue) && parseFloat(bValue)) {
          a = parseFloat(aValue)
          b = parseFloat(bValue)
          return a < b ? -1 : a > b ? 1 : 0;
        }
      }

      if (
          dayjs(aValue, 'yyyy MMM').isValid() &&
          dayjs(bValue, 'yyyy MMM').isValid()
      ) {
        a = parseInt(
            aValue.split(' ')[0] +
            (_monthShortNames.indexOf(aValue.split(' ')[1]) + 10)
        );
        b = parseInt(
            bValue.split(' ')[0] +
            (_monthShortNames.indexOf(bValue.split(' ')[1]) + 10)
        );
        return a < b ? -1 : a > b ? 1 : 0;
      } else if (
          dayjs(aValue, 'yyyy MMMM').isValid() &&
          dayjs(bValue, 'yyyy MMMM').isValid()
      ) {
        a = parseInt(
            aValue.split(' ')[0] +
            (_monthFullNames.indexOf(aValue.split(' ')[1]) + 10)
        );
        b = parseInt(
            bValue.split(' ')[0] +
            (_monthFullNames.indexOf(bValue.split(' ')[1]) + 10)
        );
        return a < b ? -1 : a > b ? 1 : 0;
      } else if (
          dayjs(aValue, 'MMM').isValid() &&
          dayjs(bValue, 'MMM').isValid()
      ) {
        a = _monthShortNames.indexOf(aValue) + 10;
        b = _monthShortNames.indexOf(bValue) + 10;
        return a < b ? -1 : a > b ? 1 : 0;
      } else if (
          dayjs(aValue, 'MMMM').isValid() &&
          dayjs(bValue, 'MMMM').isValid()
      ) {
        a = _monthFullNames.indexOf(aValue) + 10;
        b = _monthFullNames.indexOf(bValue) + 10;
        return a < b ? -1 : a > b ? 1 : 0;
      } else if (
          dayjs(aValue, 'MMMM, YYYY').isValid() &&
          dayjs(bValue, 'MMMM, YYYY').isValid()
      ) {
        a = parseInt(
            aValue.split(', ')[1] +
            (_monthFullNames.indexOf(aValue.split(', ')[0]) + 10)
        );
        b = parseInt(
            bValue.split(', ')[1] +
            (_monthFullNames.indexOf(bValue.split(', ')[0]) + 10)
        );
        return a < b ? -1 : a > b ? 1 : 0;
      }

      if (field && field.sortType === 'MONTH1') {
        const monthNameArr = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        a = parseInt(
            aValue.split(' ')[0] +
            (monthNameArr.indexOf(aValue.split(' ')[1]) + 10)
        );
        b = parseInt(
            bValue.split(' ')[0] +
            (monthNameArr.indexOf(bValue.split(' ')[1]) + 10)
        );
        return a < b ? -1 : a > b ? 1 : 0;
      } else if (field && field.sortType === 'MONTH2') {
        const fullMonthNameArr = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ];
        a =
            aValue.split(', ').length > 1
                ? parseInt(
                    aValue.split(', ')[1] +
                    (fullMonthNameArr.indexOf(aValue.split(', ')[0]) + 10)
                )
                : 100;
        b =
            bValue.split(', ').length > 1
                ? parseInt(
                    bValue.split(', ')[1] +
                    (fullMonthNameArr.indexOf(bValue.split(', ')[0]) + 10)
                )
                : 100;
        return a < b ? -1 : a > b ? 1 : 0;
      }
      return null;
    },
  },
};

const _monthFullNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const _monthShortNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
</script>

<style scoped></style>